import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  SignalIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { adminService } from '../../services/adminService';
import { getSseAccessToken } from '../../utils/sseAuthToken';

type PeriodMode = 'today' | 'all' | 'range';

interface SmsDashboardStats {
  campaigns: Array<{ id: string; name: string; status: string }>;
  stats: {
    activeTextCampaigns: number;
    sentToday: number;
    deliveredToday: number;
    failedToday: number;
    pendingToday: number;
    inboundToday: number;
    openThreads: number;
    dueEnrollments: number;
    waitingDelivery: number;
  };
  statusBreakdown: {
    delivered: number;
    sent: number;
    queued: number;
    failed: number;
    undelivered: number;
    inbound: number;
    total: number;
  };
  chartMetrics: {
    deliveryRate: string;
    replyRate: string;
  };
}

interface SmsDashboardEvent {
  id: string;
  leadId: string;
  leadName: string | null;
  leadPhone: string | null;
  campaignId: string;
  campaignName: string;
  sequenceStepNumber: number | null;
  messageType: string;
  direction: string;
  status: string;
  bodyPreview: string;
  createdAt: string;
  enrollmentStatus: string | null;
  pauseReason: string | null;
}

interface EventsPage {
  events: SmsDashboardEvent[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

const EVENTS_PAGE_SIZE = 25;

const STATUS_COLORS: Record<string, string> = {
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  queued: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  undelivered: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  received: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
};

const CHART_ITEMS: Array<{ key: keyof SmsDashboardStats['statusBreakdown']; label: string; color: string }> = [
  { key: 'delivered', label: 'Delivered', color: 'bg-green-100 dark:bg-green-900/30' },
  { key: 'sent', label: 'Sent', color: 'bg-blue-100 dark:bg-blue-900/30' },
  { key: 'queued', label: 'Queued', color: 'bg-slate-200 dark:bg-slate-800/40' },
  { key: 'failed', label: 'Failed', color: 'bg-red-100 dark:bg-red-900/30' },
  { key: 'undelivered', label: 'Undelivered', color: 'bg-orange-100 dark:bg-orange-900/30' },
  { key: 'inbound', label: 'Inbound replies', color: 'bg-purple-100 dark:bg-purple-900/30' },
];

const PERIOD_TABS: Array<{ id: PeriodMode; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'all', label: 'All time' },
  { id: 'range', label: 'Custom range' },
];

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function periodLabel(mode: PeriodMode, fromDate: string, toDate: string): string {
  if (mode === 'today') return 'Today';
  if (mode === 'all') return 'All time';
  if (fromDate === toDate) return fromDate;
  return `${fromDate} → ${toDate}`;
}

function buildQueryParams(
  periodMode: PeriodMode,
  selectedCampaign: string,
  fromDate: string,
  toDate: string
) {
  const params: {
    campaignId?: string;
    startDate?: string;
    endDate?: string;
    allTime?: boolean;
    period?: PeriodMode;
  } = {};
  if (selectedCampaign) params.campaignId = selectedCampaign;
  params.period = periodMode;
  if (periodMode === 'all') {
    params.allTime = true;
  } else {
    params.startDate = `${fromDate}T00:00:00.000Z`;
    params.endDate = `${toDate}T23:59:59.999Z`;
  }
  return params;
}

const SmsLiveMonitorPage: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [statsData, setStatsData] = useState<SmsDashboardStats | null>(null);
  const [eventsPage, setEventsPage] = useState<EventsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('today');
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [eventPageNum, setEventPageNum] = useState(1);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const closedRef = useRef(false);
  const eventsRefreshTimerRef = useRef<number | null>(null);

  const queryParams = useMemo(
    () => buildQueryParams(periodMode, selectedCampaign, fromDate, toDate),
    [periodMode, selectedCampaign, fromDate, toDate]
  );

  const streamUrlBase = useMemo(() => {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const fullUrl = baseUrl.startsWith('http')
      ? `${baseUrl}/admin/sms-dashboard/stream`
      : `${window.location.origin}${baseUrl}/admin/sms-dashboard/stream`;
    const url = new URL(fullUrl);
    if (queryParams.campaignId) url.searchParams.set('campaignId', queryParams.campaignId);
    if (queryParams.allTime) {
      url.searchParams.set('allTime', 'true');
      url.searchParams.set('period', 'all');
    } else {
      if (queryParams.startDate) url.searchParams.set('startDate', queryParams.startDate);
      if (queryParams.endDate) url.searchParams.set('endDate', queryParams.endDate);
      url.searchParams.set('period', periodMode);
    }
    return url.toString();
  }, [queryParams, periodMode]);

  const loadEvents = useCallback(
    async (page: number, silent = false) => {
      if (!silent) setEventsLoading(true);
      try {
        const data = (await adminService.getSmsDashboardEvents({
          ...queryParams,
          page,
          limit: EVENTS_PAGE_SIZE,
        })) as EventsPage;
        setEventsPage(data);
      } catch {
        if (!silent) setEventsPage(null);
      } finally {
        if (!silent) setEventsLoading(false);
      }
    },
    [queryParams]
  );

  useEffect(() => {
    setEventPageNum(1);
  }, [periodMode, selectedCampaign, fromDate, toDate]);

  useEffect(() => {
    void loadEvents(eventPageNum);
  }, [eventPageNum, loadEvents]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('EventSource' in window)) return;

    closedRef.current = false;
    setLoading(true);

    const cleanup = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      setConnected(false);
    };

    const scheduleReconnect = () => {
      if (closedRef.current || reconnectTimerRef.current) return;
      if (document.visibilityState !== 'visible') return;
      reconnectAttemptsRef.current += 1;
      const delay = Math.min(30_000, 1000 * 2 ** (reconnectAttemptsRef.current - 1));
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        void connect();
      }, delay);
    };

    const connect = async () => {
      cleanup();
      if (closedRef.current) return;
      if (document.visibilityState !== 'visible') {
        scheduleReconnect();
        return;
      }

      const token = await getSseAccessToken();
      if (!token) {
        scheduleReconnect();
        return;
      }

      const url = new URL(streamUrlBase);
      url.searchParams.set('token', token);
      const es = new EventSource(url.toString());
      eventSourceRef.current = es;

      es.addEventListener('open', () => {
        reconnectAttemptsRef.current = 0;
        setConnected(true);
        setLoading(false);
      });

      es.addEventListener('sms-dashboard', (ev) => {
        try {
          const parsed = JSON.parse((ev as MessageEvent).data) as SmsDashboardStats;
          setStatsData(parsed);
          setLoading(false);

          if (periodMode === 'today' && eventPageNum === 1) {
            if (eventsRefreshTimerRef.current) {
              window.clearTimeout(eventsRefreshTimerRef.current);
            }
            eventsRefreshTimerRef.current = window.setTimeout(() => {
              void loadEvents(1, true);
            }, 400);
          }
        } catch {
          // ignore parse errors
        }
      });

      es.addEventListener('ping', () => {
        // keepalive
      });

      es.onerror = () => {
        setConnected(false);
        es.close();
        eventSourceRef.current = null;
        if (closedRef.current) return;
        scheduleReconnect();
      };
    };

    const handleVisibility = () => {
      if (closedRef.current) return;
      if (document.visibilityState === 'visible') {
        if (!eventSourceRef.current) void connect();
      } else {
        cleanup();
      }
    };

    void connect();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      closedRef.current = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      if (eventsRefreshTimerRef.current) {
        window.clearTimeout(eventsRefreshTimerRef.current);
      }
      cleanup();
    };
  }, [streamUrlBase, periodMode, eventPageNum, loadEvents]);

  const stats = statsData?.stats;
  const breakdown = statsData?.statusBreakdown;
  const chartMetrics = statsData?.chartMetrics;
  const campaigns = statsData?.campaigns ?? [];
  const events = eventsPage?.events ?? [];
  const visibleTotal = Math.max(breakdown?.total ?? 0, 1);

  const updateIntervalLabel =
    periodMode === 'all' ? 'updates every ~20s' : 'updates every ~12s';

  const exportCsv = () => {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const url = new URL(
      baseUrl.startsWith('http')
        ? `${baseUrl}/admin/sms-messages/export`
        : `${window.location.origin}${baseUrl}/admin/sms-messages/export`
    );
    if (selectedCampaign) url.searchParams.set('campaignId', selectedCampaign);
    if (periodMode !== 'all') {
      if (fromDate) url.searchParams.set('from', `${fromDate}T00:00:00.000Z`);
      if (toDate) url.searchParams.set('to', `${toDate}T23:59:59.999Z`);
    }
    url.searchParams.set('limit', '5000');
    fetch(url.toString(), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'sms-messages.csv';
        a.click();
      })
      .catch(() => undefined);
  };

  const rangeSummary = periodLabel(periodMode, fromDate, toDate);
  const totalEvents = eventsPage?.total ?? 0;
  const currentPage = eventsPage?.page ?? eventPageNum;
  const totalPages = eventsPage?.totalPages ?? 0;
  const rangeStart = totalEvents === 0 ? 0 : (currentPage - 1) * EVENTS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * EVENTS_PAGE_SIZE, totalEvents);

  if (loading && !statsData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      <div className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-7 w-7 text-primary" />
              SMS Live Monitor
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {rangeSummary} · {updateIntervalLabel}
              <span
                className={`ml-2 inline-flex items-center gap-1 text-xs ${
                  connected ? 'text-green-600' : 'text-amber-600'
                }`}
              >
                <SignalIcon className="h-3.5 w-3.5" />
                {connected ? 'Live' : 'Reconnecting…'}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted/50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {campaigns.length > 0 && (
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All text campaigns</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.status})
                </option>
              ))}
            </select>
          )}

          <div className="inline-flex rounded-lg border border-border bg-muted/30 p-0.5">
            {PERIOD_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPeriodMode(tab.id)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  periodMode === tab.id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {periodMode === 'range' && (
            <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5">
              <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
                From
                <input
                  type="date"
                  value={fromDate}
                  max={toDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </label>
              <span className="text-muted-foreground">—</span>
              <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
                To
                <input
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard label="Active campaigns" value={stats?.activeTextCampaigns ?? 0} />
        <KpiCard label="Sent" value={stats?.sentToday ?? 0} />
        <KpiCard
          label="Delivered"
          value={stats?.deliveredToday ?? 0}
          icon={<CheckCircleIcon className="h-4 w-4 text-green-600" />}
        />
        <KpiCard
          label="Failed"
          value={stats?.failedToday ?? 0}
          icon={<XCircleIcon className="h-4 w-4 text-red-600" />}
        />
        <KpiCard label="Inbound" value={stats?.inboundToday ?? 0} />
        <KpiCard label="Pending" value={stats?.pendingToday ?? 0} />
        <KpiCard label="Open threads" value={stats?.openThreads ?? 0} />
        <KpiCard
          label="Due now"
          value={stats?.dueEnrollments ?? 0}
          icon={<ClockIcon className="h-4 w-4 text-amber-600" />}
        />
        <KpiCard label="Awaiting delivery" value={stats?.waitingDelivery ?? 0} />
      </div>

      <Card className="card-elevated">
        <div className="px-5 py-4 border-b border-border bg-muted/50">
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-primary" />
            SMS Activity Breakdown
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Message volume for {rangeSummary}
          </p>
        </div>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">
                {chartMetrics?.deliveryRate ?? '0%'}
              </div>
              <div className="text-xs font-medium text-muted-foreground mt-1">Delivery rate</div>
              <div className="text-[11px] text-muted-foreground/80">delivered ÷ sent outbound</div>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
              <div className="text-xl font-bold text-foreground leading-tight">
                {chartMetrics?.replyRate ?? '0%'}
              </div>
              <div className="text-xs font-medium text-muted-foreground mt-1">Reply rate</div>
              <div className="text-[11px] text-muted-foreground/80">inbound ÷ sent outbound</div>
            </div>
          </div>

          {!breakdown || breakdown.total === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No SMS activity in selected period
            </div>
          ) : (
            <div className="space-y-3">
              {CHART_ITEMS.map(({ key, label, color }) => {
                const count = breakdown[key] ?? 0;
                const pct = Math.round((count / visibleTotal) * 100);
                return (
                  <div key={key} className="flex items-center gap-3">
                    <div className="w-32 flex-shrink-0">
                      <span
                        className={`inline-flex text-xs px-2 py-0.5 rounded-full font-medium ${
                          STATUS_COLORS[key === 'inbound' ? 'received' : key] ||
                          'bg-muted text-muted-foreground'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${color}`}
                        style={{ width: `${Math.max(pct, count > 0 ? 2 : 0)}%` }}
                      />
                    </div>
                    <div className="w-16 text-right">
                      <span className="text-sm font-semibold text-foreground">{count}</span>
                      <span className="text-xs text-muted-foreground ml-1">({pct}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <div className="text-sm text-muted-foreground">
            {totalEvents === 0
              ? 'No events'
              : `Showing ${rangeStart}–${rangeEnd} of ${totalEvents.toLocaleString()}`}
            {totalPages > 0 && (
              <span className="ml-2">· Page {currentPage} of {totalPages}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outlined"
              size="sm"
              disabled={currentPage <= 1 || eventsLoading}
              onClick={() => setEventPageNum((p) => Math.max(1, p - 1))}
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outlined"
              size="sm"
              disabled={!eventsPage?.hasMore || eventsLoading}
              onClick={() => setEventPageNum((p) => p + 1)}
            >
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <CardContent className="p-0 overflow-x-auto relative">
          {eventsLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
              <LoadingSpinner size="sm" />
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Lead</th>
                <th className="px-4 py-3 font-medium">Campaign</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Preview</th>
                <th className="px-4 py-3 font-medium">Time</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && !eventsLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                    No SMS events in selected period
                  </td>
                </tr>
              ) : (
                events.map((ev) => (
                  <tr key={ev.id} className="border-b border-border/60 hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{ev.leadName || '—'}</div>
                      <div className="text-xs text-muted-foreground">{ev.leadPhone}</div>
                    </td>
                    <td className="px-4 py-3">{ev.campaignName}</td>
                    <td className="px-4 py-3">{ev.messageType}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          STATUS_COLORS[ev.status] || 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {ev.status}
                      </span>
                      {ev.pauseReason && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">{ev.pauseReason}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate text-muted-foreground">{ev.bodyPreview}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{formatTime(ev.createdAt)}</td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/sms/inbox?leadId=${ev.leadId}`}
                        className="text-primary hover:underline text-xs"
                      >
                        Inbox
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

function KpiCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold text-foreground mt-1 flex items-center gap-2">
        {value}
        {icon}
      </div>
    </Card>
  );
}

export default SmsLiveMonitorPage;
